import { makeProviderKey, type ProviderKey } from '@littlebird/integrations';
import {
  type CachedMCPServer,
  extractMCPErrorMessage,
  getIntegrationDisplayStatus,
  hasConnectedMcpStatus,
  type MCPCatalogEntry,
} from '@littlebird/mcp';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons, type IconName } from '@/assets/icons/icons';
import IMessageLogo from '@/assets/images/channels/imessage.png';
import WhatsAppLogo from '@/assets/images/channels/whatsapp.png';
import { backendApis } from '@/backendApis';
import {
  ModalsManager,
  type BaseModalFCProps,
  type ModalFC,
} from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Empty } from '@/components/ui/EmptyState';
import { IconButton } from '@/components/ui/IconButton';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { AXON_MANIFEST_TOOLKIT_IDS } from '@/features/integration-management/integration-management.adapters';
import {
  useIntegrationGrid,
  type GridItem,
  type GridSection,
  type Selection,
  type StatusFilter,
} from '@/features/integrations/add-integrations.hooks';
import {
  ON_DEVICE_SELECTION,
  SUGGESTED_SELECTION,
  type IntegrationState,
  type IntegrationSuggestion,
} from '@/features/integrations/integrations.types';
import {
  useActiveSectionSlug,
  useSectionScrollSpy,
  type ActiveSlugStore,
} from '@/features/integrations/use-section-scroll-spy';
import { LocalMcpAddServerModal } from '@/features/local-mcp';
import { MCPAddServerModal } from '@/features/mcp/MCPAddServerModal';
import {
  MESSAGING_CHANNEL_DESCRIPTIONS,
  MESSAGING_CHANNEL_LABELS,
  MessagingChannelTypes,
  type MessagingChannelType,
} from '@/features/msg-channels/common/msg-channels.types';
import { IMessageChannelConnectModal } from '@/features/msg-channels/imessage/IMessageChannelConnectModal';
import { WhatsAppChannelConnectModal } from '@/features/msg-channels/whatsapp/WhatsAppChannelConnectModal';
import { integrationManagementSearchId } from '@/features/settings/settings-search-dom';
import { useHashRoute } from '@/hooks/useHashRoute';
import { isConnectableDeviceToolkit } from '@/integrations/device-integrations';
import { IntegrationIcon } from '@/integrations/integration-icon';
import { integrationsStore } from '@/integrations/integrations.store';
import {
  hasIntegrationAccess,
  trackOnDeviceConnectClicked,
  trackOnDeviceTabViewed,
} from '@/integrations/integrations.utils';
import {
  useIntegrationActions,
  type HandleIntegrationAction,
} from '@/integrations/useIntegrationActions';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { handleExternalUrlClick } from '@/utils/platform';

import type { ServerEntry } from '@littlebird/axon-mcp';

const filterLabels: Record<StatusFilter, string> = {
  all: 'All',
  connected: 'Connected',
  not_connected: 'Not Connected',
};

interface AddIntegrationsModalProps extends BaseModalFCProps {
  initialSelection?: Selection;
}

export const AddIntegrationsModal: ModalFC<AddIntegrationsModalProps> =
  observer(({ open, onOpenChange, initialSelection }) => {
    const { handleAction } = useIntegrationActions('integrations_modal');
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<StatusFilter>('all');

    const {
      isLoading,
      loadError,
      mcpLoadError,
      retryLoad,
      retryMCP,
      mcpEnabled,
      customMcpEnabled,
      visibleItems,
      sections,
      dismissSuggestion,
    } = useIntegrationGrid({
      search,
      filter,
      selection: 'all',
    });

    const sectionSlugs = useMemo(
      () => sections.map((section) => section.slug),
      [sections]
    );
    const { setContainer, registerSection, activeSlugStore, scrollToSection } =
      useSectionScrollSpy(sectionSlugs);

    const onDeviceTabTrackedRef = useRef(false);
    useEffect(() => {
      const handleActiveChange = () => {
        if (activeSlugStore.get() === ON_DEVICE_SELECTION) {
          if (onDeviceTabTrackedRef.current) return;
          onDeviceTabTrackedRef.current = true;
          trackOnDeviceTabViewed();
        } else {
          onDeviceTabTrackedRef.current = false;
        }
      };
      handleActiveChange();
      return activeSlugStore.subscribe(handleActiveChange);
    }, [activeSlugStore]);

    const { setHash } = useHashRoute();
    const openSettings = (providerKey?: ProviderKey) => {
      onOpenChange?.(false);
      setHash(
        providerKey
          ? `settings/Integrations/${integrationManagementSearchId(providerKey)}`
          : 'settings/Integrations'
      );
    };

    // Close this grid when launching a flow, so finishing/cancelling it returns
    // to the integrations page underneath rather than back here
    const dismiss = () => onOpenChange?.(false);

    const hasFeature = useFeature();
    const requiresUpgrade =
      !hasIntegrationAccess() && !hasFeature(FEATURES.WEB_ONBOARDING_FEED);
    const openUpgrade = (source: string) => {
      trackEvent('UPGRADE_BUTTON_CLICKED', { source });
      onOpenChange?.(false);
      setHash('settings/Subscription');
    };

    const searching = search.trim().length > 0;

    // Rail clicks during a search first clear it, then scroll once the
    // sections are back in the DOM
    const pendingScrollSlugRef = useRef<string | null>(null);
    const selectRail = (slug: string) => {
      if (searching) {
        pendingScrollSlugRef.current = slug;
        setSearch('');
      } else {
        scrollToSection(slug);
      }
    };
    useEffect(() => {
      if (searching || !pendingScrollSlugRef.current) return;
      scrollToSection(pendingScrollSlugRef.current, 'auto');
      pendingScrollSlugRef.current = null;
    }, [searching, scrollToSection]);

    const initialScrolledRef = useRef(false);
    useEffect(() => {
      if (initialScrolledRef.current || sections.length === 0) return;
      if (initialSelection) {
        // The target section may not have streamed in yet — keep waiting
        // instead of burning the one-shot on an early partial load
        if (!sections.some((section) => section.slug === initialSelection)) {
          return;
        }
        scrollToSection(initialSelection, 'auto');
      }
      initialScrolledRef.current = true;
    }, [sections, initialSelection, scrollToSection]);

    const empty = search
      ? {
          title: 'No matches found',
          description: 'Try a different search term.',
        }
      : filter === 'connected'
        ? {
            title: 'No integrations connected',
            description: 'Connect an integration to see it here.',
          }
        : {
            title: 'Nothing to show',
            description: 'Check back later for more integrations.',
          };

    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Content className="max-w-5xl h-[85vh] bg-background-subtle p-0 flex flex-col overflow-hidden">
          <YStack className="flex-1 min-h-0">
            <YStack className="px-8 pt-8 pb-6 flex-shrink-0">
              <Dialog.Title className="text-2xl text-foreground font-serif">
                Add integrations
              </Dialog.Title>
            </YStack>

            <XStack className="flex-1 min-h-0 items-stretch">
              <SectionRail
                sections={sections}
                activeSlugStore={activeSlugStore}
                searching={searching}
                onSelect={selectRail}
              />

              <YStack className="self-start w-px h-[calc(100%-2rem)] bg-border-subtle" />

              <YStack className="flex-1 min-h-0 gap-8 px-8 pb-8">
                <XStack className="gap-3 items-center flex-shrink-0">
                  <div className="relative flex-1">
                    <Icons.search className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-subtle size-4" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search integrations..."
                      className="pl-10 h-9"
                      autoFocus
                    />
                  </div>
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <Button variant="outline" className="h-9 shrink-0">
                        {filterLabels[filter]}
                        <Icons.chevronDown className="size-3.5" />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end" fit>
                      <DropdownMenu.Item onSelect={() => setFilter('all')}>
                        All
                        {filter === 'all' && (
                          <Icons.check className="size-3.5" />
                        )}
                      </DropdownMenu.Item>
                      <DropdownMenu.Item
                        onSelect={() => setFilter('connected')}
                      >
                        Connected
                        {filter === 'connected' && (
                          <Icons.check className="size-3.5" />
                        )}
                      </DropdownMenu.Item>
                      <DropdownMenu.Item
                        onSelect={() => setFilter('not_connected')}
                      >
                        Not Connected
                        {filter === 'not_connected' && (
                          <Icons.check className="size-3.5" />
                        )}
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                  {mcpEnabled && customMcpEnabled && (
                    <Button
                      className="h-9 shrink-0"
                      onClick={() => {
                        if (requiresUpgrade) {
                          openUpgrade('add_custom_mcp_button');
                          return;
                        }
                        dismiss();
                        ModalsManager.show(MCPAddServerModal, {});
                      }}
                    >
                      <Icons.plus className="size-3.5" />
                      Add custom
                      {requiresUpgrade && <PlusBadge />}
                    </Button>
                  )}
                </XStack>

                <YStack className="flex-1 min-h-0">
                  {/* -mx-2 px-2 gives the grids room to bleed 8px with -mx-2,
                      which cancels the cards' p-2 so their icons line up
                      with the search field and section labels */}
                  <ScrollMask
                    ref={setContainer}
                    className="-mx-2 h-full px-2 scrollbar-hide"
                    showEndFade
                  >
                    {isLoading ? (
                      <YStack center className="py-12 gap-2">
                        <Icons.loader className="size-4 animate-spin text-foreground-muted" />
                        <p className="text-foreground-muted text-xs">
                          Loading integrations...
                        </p>
                      </YStack>
                    ) : loadError ? (
                      <Empty.Root variant="plain" className="h-full">
                        <Empty.Media variant="icon">
                          <Icons.warnTriangle />
                        </Empty.Media>
                        <Empty.Header>
                          <Empty.Title>Couldn't load integrations</Empty.Title>
                          <Empty.Description>
                            {extractMCPErrorMessage(
                              loadError,
                              'Something went wrong while loading integrations.'
                            )}
                          </Empty.Description>
                        </Empty.Header>
                        <Empty.Actions>
                          <Button size="sm" onClick={retryLoad}>
                            Try again
                          </Button>
                        </Empty.Actions>
                      </Empty.Root>
                    ) : (
                      <>
                        {mcpLoadError && (
                          <div className="mb-4 flex items-center justify-between gap-4 rounded-2xl border border-border bg-surface px-4 py-3 dark:border-border-strong">
                            <YStack className="gap-0.5 min-w-0">
                              <span className="text-sm font-medium text-foreground">
                                Couldn't load MCP servers
                              </span>
                              <span className="text-xs text-foreground-muted line-clamp-1">
                                {extractMCPErrorMessage(
                                  mcpLoadError,
                                  'Something went wrong loading MCP servers.'
                                )}
                              </span>
                            </YStack>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={retryMCP}
                            >
                              Try again
                            </Button>
                          </div>
                        )}
                        {visibleItems.length === 0 ? (
                          <Empty.Root variant="plain" className="h-full">
                            <Empty.Media variant="icon">
                              {search ? <Icons.search /> : <Icons.link />}
                            </Empty.Media>
                            <Empty.Header>
                              <Empty.Title>{empty.title}</Empty.Title>
                              <Empty.Description>
                                {empty.description}
                              </Empty.Description>
                            </Empty.Header>
                          </Empty.Root>
                        ) : searching ? (
                          <div className="-mx-2 grid grid-cols-2 gap-5 pb-6">
                            {visibleItems.map((item) => (
                              <GridItemCard
                                key={gridItemKey(item)}
                                item={item}
                                handleAction={handleAction}
                                openSettings={openSettings}
                                requiresUpgrade={requiresUpgrade}
                                openUpgrade={openUpgrade}
                                dismiss={dismiss}
                                dismissSuggestion={dismissSuggestion}
                              />
                            ))}
                          </div>
                        ) : (
                          <YStack className="gap-10 pb-6">
                            {sections.map((section) => (
                              <YStack
                                key={section.slug}
                                ref={registerSection(section.slug)}
                                className="gap-4"
                              >
                                <h3 className="text-sm font-medium text-foreground-muted">
                                  {section.label}
                                </h3>
                                <div className="-mx-2 grid grid-cols-2 gap-5">
                                  {section.items.map((item) => (
                                    <GridItemCard
                                      key={gridItemKey(item)}
                                      item={item}
                                      handleAction={handleAction}
                                      openSettings={openSettings}
                                      requiresUpgrade={requiresUpgrade}
                                      openUpgrade={openUpgrade}
                                      dismiss={dismiss}
                                      dismissSuggestion={dismissSuggestion}
                                    />
                                  ))}
                                </div>
                              </YStack>
                            ))}
                          </YStack>
                        )}
                      </>
                    )}
                  </ScrollMask>
                </YStack>
              </YStack>
            </XStack>
          </YStack>
        </Dialog.Content>
      </Dialog.Root>
    );
  });

const isPseudoSlug = (slug: string) =>
  slug === SUGGESTED_SELECTION || slug === ON_DEVICE_SELECTION;

// Subscribes to the active slug itself so scroll-position updates re-render
// only this small rail, not the whole card grid
const SectionRail = ({
  sections,
  activeSlugStore,
  searching,
  onSelect,
}: {
  sections: GridSection[];
  activeSlugStore: ActiveSlugStore;
  searching: boolean;
  onSelect: (slug: string) => void;
}) => {
  const activeSlug = useActiveSectionSlug(activeSlugStore);
  const pseudoSections = sections.filter((s) => isPseudoSlug(s.slug));
  const categorySections = sections.filter((s) => !isPseudoSlug(s.slug));
  return (
    <YStack className="w-[15rem] flex-shrink-0 px-4 pb-8 overflow-y-auto scrollbar-hide">
      <YStack className="gap-1">
        {pseudoSections.map((section) => (
          <RailItem
            key={section.slug}
            icon={section.icon}
            label={section.label}
            selected={!searching && activeSlug === section.slug}
            onClick={() => onSelect(section.slug)}
          />
        ))}
      </YStack>
      {pseudoSections.length > 0 && categorySections.length > 0 && (
        <YStack className="mt-3 mb-3 mx-2 h-px bg-border-subtle" />
      )}
      <YStack className="gap-1">
        {categorySections.map((section) => (
          <RailItem
            key={section.slug}
            icon={section.icon}
            label={section.label}
            selected={!searching && activeSlug === section.slug}
            onClick={() => onSelect(section.slug)}
          />
        ))}
      </YStack>
    </YStack>
  );
};

export const gridItemKey = (item: GridItem): string => {
  switch (item.type) {
    case 'integration':
      return item.integration.info.toolkit_name;
    case 'mcp_server':
      return item.server.id;
    case 'mcp_catalog':
      return item.entry.id;
    case 'local_mcp_template':
      return `template:${item.entry.id}`;
    case 'bundled_mcp_template':
      return `bundled:${item.template.id}`;
    case 'guided_custom_mcp':
      return item.offer.id;
    case 'channel':
      return `channel:${item.channelType}`;
  }
};

const settingsProviderKey = (item: GridItem): ProviderKey | undefined => {
  switch (item.type) {
    case 'integration': {
      const toolkit = item.integration.info.toolkit_name;
      if (!item.integration.info.on_device) {
        return makeProviderKey('composio', toolkit);
      }
      const manifestToolkit =
        AXON_MANIFEST_TOOLKIT_IDS[
          toolkit as keyof typeof AXON_MANIFEST_TOOLKIT_IDS
        ];
      return manifestToolkit
        ? makeProviderKey('device', manifestToolkit)
        : undefined;
    }
    case 'mcp_server':
      return makeProviderKey('mcp', item.server.slug);
    case 'local_mcp_template':
    case 'bundled_mcp_template':
      return item.installedServer
        ? makeProviderKey('device', item.installedServer.slug)
        : undefined;
    case 'channel':
      return makeProviderKey('channel', item.channelType);
    default:
      return undefined;
  }
};

export const GridItemCard = observer(
  ({
    item,
    handleAction,
    openSettings,
    requiresUpgrade,
    openUpgrade,
    dismiss,
    dismissSuggestion,
  }: {
    item: GridItem;
    handleAction: HandleIntegrationAction;
    openSettings: (providerKey?: ProviderKey) => void;
    requiresUpgrade: boolean;
    openUpgrade: (source: string) => void;
    dismiss: () => void;
    dismissSuggestion: (suggestion: IntegrationSuggestion) => void;
  }) => {
    const openItemSettings = () => openSettings(settingsProviderKey(item));
    const { suggestion } = item;
    const suggested: SuggestedCardProps | undefined = suggestion && {
      reason: suggestion.reason,
      onDismiss: () => dismissSuggestion(suggestion),
    };
    if (item.type === 'integration') {
      return (
        <IntegrationCard
          integration={item.integration}
          suggested={suggested}
          isPending={integrationsStore.isPending(
            item.integration.info.toolkit_name
          )}
          handleAction={handleAction}
          openSettings={openItemSettings}
          requiresUpgrade={
            requiresUpgrade && item.integration.info.requires_paid_plan
          }
          openUpgrade={openUpgrade}
        />
      );
    }
    if (item.type === 'mcp_server') {
      return (
        <MCPServerCard
          server={item.server}
          description={item.description}
          iconUrl={item.iconUrl}
          openSettings={openItemSettings}
          dismiss={dismiss}
        />
      );
    }
    if (item.type === 'local_mcp_template') {
      return (
        <McpTemplateCard
          id={item.entry.id}
          name={item.entry.name}
          description={item.entry.description}
          iconUrl={item.entry.icon_url}
          installedServer={item.installedServer}
          requiresUpgrade={requiresUpgrade}
          upgradeSource={`local_mcp_template_card:${item.entry.id}`}
          openUpgrade={openUpgrade}
          openSettings={openItemSettings}
          dismiss={dismiss}
          onConnect={() => {
            trackOnDeviceConnectClicked(
              item.entry.id,
              'catalog',
              'integrations_modal'
            );
            ModalsManager.show(LocalMcpAddServerModal, {
              catalogEntry: item.entry,
            });
          }}
        />
      );
    }
    if (item.type === 'bundled_mcp_template') {
      return (
        <McpTemplateCard
          id={item.template.id}
          name={item.template.name}
          description={item.template.description}
          installedServer={item.installedServer}
          requiresUpgrade={requiresUpgrade}
          upgradeSource={`bundled_mcp_template_card:${item.template.id}`}
          openUpgrade={openUpgrade}
          openSettings={openItemSettings}
          dismiss={dismiss}
          onConnect={() => {
            trackOnDeviceConnectClicked(
              item.template.id,
              'bundled',
              'integrations_modal'
            );
            ModalsManager.show(LocalMcpAddServerModal, {
              bundledTemplate: item.template,
            });
          }}
        />
      );
    }
    if (item.type === 'guided_custom_mcp') {
      const { offer } = item;
      const OfferIcon = Icons[offer.icon];
      const install = () => {
        dismiss();
        void offer.connect();
      };
      const upgrade = () => openUpgrade(`integration_card:${offer.id}`);
      const action = requiresUpgrade ? (
        <UpgradeButton onClick={upgrade} />
      ) : (
        <ConnectButton onClick={install} />
      );
      return (
        <Card
          icon={<OfferIcon className="size-full" />}
          name={offer.name}
          description={offer.description}
          action={action}
          gated={requiresUpgrade}
          onClick={requiresUpgrade ? upgrade : install}
        />
      );
    }
    if (item.type === 'channel') {
      return (
        <ChannelCard
          channelType={item.channelType}
          isConnected={item.isConnected}
          openSettings={openItemSettings}
          dismiss={dismiss}
        />
      );
    }
    return (
      <MCPCatalogCard
        entry={item.entry}
        suggested={suggested}
        requiresUpgrade={requiresUpgrade && item.entry.requires_paid_plan}
        openUpgrade={openUpgrade}
        dismiss={dismiss}
      />
    );
  }
);

const RailItem = ({
  icon,
  label,
  selected,
  onClick,
}: {
  icon: IconName;
  label: string;
  selected: boolean;
  onClick: () => void;
}) => {
  const Icon = Icons[icon];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'flex items-center gap-3 w-full text-left px-2.5 py-2 rounded-lg text-sm',
        selected
          ? 'bg-surface-active text-foreground font-medium'
          : 'text-foreground-subtle hover:bg-surface-hover hover:text-foreground'
      )}
    >
      <Icon className="size-4 shrink-0 text-foreground-muted" />
      <span className="flex-1 truncate">{label}</span>
    </button>
  );
};

// A card in the Suggested section shows why it is suggested, and can be dismissed
type SuggestedCardProps = { reason: string; onDismiss: () => void };

export const Card = ({
  icon,
  badge,
  name,
  description,
  action,
  gated,
  onClick,
  suggested,
}: {
  /** Fills the icon container edge to edge; the container clips its corners. */
  icon: React.ReactNode;
  /** Absolutely positioned over the icon container, outside its clip. */
  badge?: React.ReactNode;
  name: React.ReactNode;
  description: string;
  action: React.ReactNode;
  gated?: boolean;
  onClick?: () => void;
  suggested?: SuggestedCardProps;
}) => (
  <XStack
    className={cn(
      'group items-center gap-4 rounded-2xl p-2 transition-colors hover:bg-surface-hover',
      gated && 'opacity-50 hover:opacity-100'
    )}
    onClick={onClick}
  >
    <div className="relative shrink-0">
      <div className="size-12 overflow-hidden rounded-xl border border-border bg-surface flex items-center justify-center">
        {icon}
      </div>
      {badge}
    </div>
    <YStack className="min-w-0 flex-1">
      <span className="font-medium text-foreground truncate flex-1 min-w-0">
        {name}
      </span>
      <p
        title={suggested?.reason ?? description}
        className="truncate text-sm text-foreground-muted"
      >
        {suggested?.reason ?? description}
      </p>
    </YStack>
    {suggested ? <DismissButton onClick={suggested.onDismiss} /> : null}
    {action}
  </XStack>
);

const DismissButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    variant="ghost"
    size="sm"
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
    className="shrink-0 text-foreground-muted hover:text-foreground"
  >
    Dismiss
  </Button>
);

const PlusBadge = () => (
  <span className="text-xs rounded-full px-2 py-0.5 font-medium shrink-0 text-slate-600 bg-slate-100 dark:text-slate-300 dark:bg-slate-800/40">
    $ Plus
  </span>
);

export const ConnectButton = ({
  onClick,
  isPending,
  label = 'Connect',
}: {
  onClick: () => void;
  isPending?: boolean;
  label?: string;
}) =>
  label === 'Connect' ? (
    <IconButton
      icon={Icons.plus}
      variant="ghost"
      size="lg"
      scale={isPending ? 1 : 1.25}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      disabled={isPending}
      isLoading={isPending}
      className="relative isolate shrink-0 hover:bg-transparent before:pointer-events-none before:absolute before:inset-1.5 before:-z-10 before:rounded-md before:content-[''] hover:before:bg-surface-active"
      title="Connect"
      aria-label="Connect"
    />
  ) : (
    <Button
      variant="outline"
      size="sm"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      disabled={isPending}
      className="shrink-0"
    >
      {isPending ? <Icons.loader className="size-3 animate-spin" /> : label}
    </Button>
  );

const UpgradeButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    variant="ghost"
    size="sm"
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
    className="shrink-0"
    aria-label="Upgrade to Plus"
  >
    <PlusBadge />
  </Button>
);

const IntegrationCard = ({
  integration,
  suggested,
  isPending,
  handleAction,
  openSettings,
  requiresUpgrade,
  openUpgrade,
}: {
  integration: IntegrationState;
  suggested?: SuggestedCardProps;
  isPending: boolean;
  handleAction: HandleIntegrationAction;
  openSettings: () => void;
  requiresUpgrade: boolean;
  openUpgrade: (source: string) => void;
}) => {
  const connectableDeviceIntegration =
    (integration.info.on_device ?? false) &&
    isConnectableDeviceToolkit(integration.info.toolkit_name);
  const isGated =
    requiresUpgrade &&
    !integration.isConnected &&
    !connectableDeviceIntegration;
  const opensSettings =
    (integration.info.on_device && !connectableDeviceIntegration) ||
    integration.isConnected;
  const install = () => handleAction(integration, 'connect');
  const upgrade = () =>
    openUpgrade(`integration_card:${integration.info.toolkit_name}`);
  const action = opensSettings ? (
    <OpenSettingsButton onClick={openSettings} />
  ) : isGated ? (
    <UpgradeButton onClick={upgrade} />
  ) : (
    <ConnectButton onClick={install} isPending={isPending} />
  );
  return (
    <Card
      icon={
        <IntegrationIcon
          iconKey={integration.info.toolkit_name}
          iconUrl={integration.info.logo_url}
          className="size-full"
        />
      }
      name={integration.info.display_name}
      description={integration.info.description}
      action={action}
      gated={isGated}
      suggested={suggested}
      onClick={
        isPending
          ? undefined
          : opensSettings
            ? openSettings
            : isGated
              ? upgrade
              : install
      }
    />
  );
};

const MCPServerCard = ({
  server,
  description,
  iconUrl,
  openSettings,
  dismiss,
}: {
  server: CachedMCPServer;
  description?: string;
  iconUrl?: string | null;
  openSettings: () => void;
  dismiss: () => void;
}) => {
  const status = getIntegrationDisplayStatus(server);
  const isConnected = hasConnectedMcpStatus(server);
  // Expired/cancelled go through reconnect (preserves the row), not fresh add
  const isReconnectableOAuth =
    (status === 'expired' || status === 'cancelled') &&
    server.auth_type === 'oauth';

  let fallbackDescription: string;
  try {
    fallbackDescription = new URL(server.url).hostname;
  } catch {
    fallbackDescription = server.url;
  }

  const onConnect = isReconnectableOAuth
    ? async () => {
        try {
          const result = await backendApis.mcp.reconnectServer(server.id);
          handleExternalUrlClick(result.redirect_url);
          // Dismiss only after reconnect succeeds, so a failure keeps the grid
          dismiss();
          ModalsManager.show(MCPAddServerModal, {
            reconnectServerId: result.server_id,
            reconnectServerName: server.name,
            reconnectFlowId: result.flow_id,
          });
        } catch (error) {
          toast.error(
            extractMCPErrorMessage(
              error,
              'Failed to reconnect. The server may be unreachable.'
            )
          );
        }
      }
    : () => {
        dismiss();
        ModalsManager.show(MCPAddServerModal, {
          initialUrl: server.url,
          initialName: server.name,
        });
      };

  const action = isConnected ? (
    <OpenSettingsButton onClick={openSettings} />
  ) : (
    <ConnectButton
      onClick={onConnect}
      label={isReconnectableOAuth ? 'Reconnect' : 'Connect'}
    />
  );

  return (
    <Card
      icon={
        <IntegrationIcon
          iconKey={server.name}
          iconUrl={iconUrl}
          className="size-full"
        />
      }
      name={server.name}
      description={description ?? fallbackDescription}
      action={action}
      onClick={isConnected ? openSettings : onConnect}
    />
  );
};

const MCPCatalogCard = ({
  entry,
  suggested,
  requiresUpgrade,
  openUpgrade,
  dismiss,
}: {
  entry: MCPCatalogEntry;
  suggested?: SuggestedCardProps;
  requiresUpgrade: boolean;
  openUpgrade: (source: string) => void;
  dismiss: () => void;
}) => {
  const install = () => {
    dismiss();
    ModalsManager.show(MCPAddServerModal, {
      initialUrl: entry.url,
      initialName: entry.name,
    });
  };
  const upgrade = () => openUpgrade(`mcp_catalog_card:${entry.id}`);
  const action = requiresUpgrade ? (
    <UpgradeButton onClick={upgrade} />
  ) : (
    <ConnectButton onClick={install} />
  );
  return (
    <Card
      icon={
        <IntegrationIcon
          iconKey={entry.id}
          iconUrl={entry.icon_url}
          className="size-full"
        />
      }
      name={entry.name}
      description={entry.description}
      action={action}
      gated={requiresUpgrade}
      suggested={suggested}
      onClick={requiresUpgrade ? upgrade : install}
    />
  );
};

const CHANNEL_ICON_SRC: Record<MessagingChannelType, string> = {
  imessage: IMessageLogo,
  whatsapp_business: WhatsAppLogo,
};

function channelConnectModalFor(channelType: MessagingChannelType) {
  return channelType === MessagingChannelTypes.IMESSAGE
    ? IMessageChannelConnectModal
    : WhatsAppChannelConnectModal;
}

export const ChannelCard = ({
  channelType,
  isConnected = false,
  openSettings = () => undefined,
  dismiss,
}: {
  channelType: MessagingChannelType;
  isConnected?: boolean;
  openSettings?: () => void;
  dismiss: () => void;
}) => {
  const install = () => {
    dismiss();
    ModalsManager.show(channelConnectModalFor(channelType), {
      currentIdentifier: null,
    });
  };

  return (
    <Card
      icon={
        <IntegrationIcon
          iconKey={channelType}
          iconUrl={CHANNEL_ICON_SRC[channelType]}
          className="size-full"
        />
      }
      name={MESSAGING_CHANNEL_LABELS[channelType]}
      description={MESSAGING_CHANNEL_DESCRIPTIONS[channelType]}
      action={
        isConnected ? (
          <OpenSettingsButton onClick={openSettings} />
        ) : (
          <ConnectButton onClick={install} />
        )
      }
      onClick={isConnected ? openSettings : install}
    />
  );
};

const McpTemplateCard = ({
  id,
  name,
  description,
  iconUrl,
  installedServer,
  requiresUpgrade,
  upgradeSource,
  openUpgrade,
  openSettings,
  dismiss,
  onConnect,
}: {
  id: string;
  name: string;
  description: string;
  iconUrl?: string | null;
  installedServer?: ServerEntry;
  requiresUpgrade: boolean;
  upgradeSource: string;
  openUpgrade: (source: string) => void;
  openSettings: () => void;
  dismiss: () => void;
  onConnect: () => void;
}) => {
  // Straight to the connect dialog. A bundled driver's download used to run
  // here behind this button's spinner, which read as a hang on a 125 MB
  // binary; the dialog owns it now and shows the bytes.
  const install = () => {
    dismiss();
    onConnect();
  };
  const upgrade = () => openUpgrade(upgradeSource);
  const action = installedServer ? (
    <OpenSettingsButton onClick={openSettings} />
  ) : requiresUpgrade ? (
    <UpgradeButton onClick={upgrade} />
  ) : (
    <ConnectButton onClick={install} />
  );
  return (
    <Card
      icon={
        <IntegrationIcon iconKey={id} iconUrl={iconUrl} className="size-full" />
      }
      name={name}
      description={description}
      action={action}
      gated={requiresUpgrade && !installedServer}
      onClick={
        installedServer ? openSettings : requiresUpgrade ? upgrade : install
      }
    />
  );
};

const OpenSettingsButton = ({ onClick }: { onClick: () => void }) => (
  <IconButton
    icon={Icons.reset}
    variant="ghost"
    size="lg"
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
    className="relative isolate shrink-0 hover:bg-transparent before:pointer-events-none before:absolute before:inset-1.5 before:-z-10 before:rounded-md before:content-[''] hover:before:bg-surface-active"
    tooltip="Manage in settings"
    aria-label="Manage in settings"
  />
);
